import {handleRepositoryDataError} from '@github-ui/data-router-error-helpers'
import {applyIssueViewerFieldValue} from '@github-ui/issue-metadata/issue-field-value-optimistic'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {
  type IssueViewerPayload,
  issueViewerPrimaryQueryOptions,
  persistIssueViewerPrimary,
} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {useQuery} from '@github-ui/react-query'
import {applyIssueSummaryDelta} from '@github-ui/sub-issues/optimistic-overlay'
import {useCallback, useMemo} from 'react'

import type {IssueReference} from '../types/issue'
import {applyPendingSuggestionOperation} from '../utils/suggestion-ref'
import {DataRouterIssueViewer} from './DataRouterIssueViewer'
import {IssueViewerLoading} from './IssueViewerLoading'
import type {OptionConfig} from './OptionConfig'
import {useTimelinePrefill} from './timeline/timeline-prefill'

const PROJECT_ITEMS_CONNECTION_PATH = ['projectItems']

type TanStackIssueViewerProps = {
  optionConfig: OptionConfig
} & IssueReference

export function getIssueViewerQueryError(query: {data: unknown; error: Error | null; isError: boolean}) {
  return query.isError && !query.data ? (query.error ?? new Error('Issue viewer query failed')) : undefined
}

export function TanStackIssueViewer(props: TanStackIssueViewerProps) {
  const {number, optionConfig} = props
  const queryIdentifier = useMemo(() => {
    if (props.isAccountIssue) return {permalink: props.permalink}
    return {owner: props.owner, repo: props.repo, number}
  }, [number, props.isAccountIssue, props.owner, props.permalink, props.repo])
  const queryOptions = useMemo(() => issueViewerPrimaryQueryOptions(queryIdentifier), [queryIdentifier])
  const query = useQuery({...queryOptions, staleTime: optionConfig.insideSidePanel ? 0 : queryOptions.staleTime})
  const payload = query.data as IssueViewerPayload | undefined
  const persistPrimaryAfterSettlement = useCallback(
    ({queryClient}: {queryClient: Parameters<typeof persistIssueViewerPrimary>[1]}) =>
      persistIssueViewerPrimary(queryIdentifier, queryClient),
    [queryIdentifier],
  )
  const mainQueryTargets = useMemo(
    () => [{queryKey: queryOptions.queryKey, onDocumentSettled: persistPrimaryAfterSettlement}],
    [persistPrimaryAfterSettlement, queryOptions.queryKey],
  )
  const issueId = payload?.data?.repository?.issue?.id
  const projectItemsConnections = useMemo(
    () => (issueId ? [{ownerId: issueId, connectionPath: PROJECT_ITEMS_CONNECTION_PATH}] : []),
    [issueId],
  )
  const renderedPayload = useOptimisticData(payload, {
    connections: projectItemsConnections,
    targets: mainQueryTargets,
    applyEntityFieldItem: applyIssueViewerFieldValue,
    applyEntityFieldValuesDelta: applyIssueSummaryDelta,
    updateEntityFieldItems: applyPendingSuggestionOperation,
  })

  const repository = renderedPayload?.data?.repository
  const issue = repository?.issue
  const timelineFrontIssue = useMemo(
    () => (issue?.timelineItems ? {id: issue.id, timelineItems: issue.timelineItems} : undefined),
    [issue],
  )
  const timelineBackIssue = useMemo(
    () => (issue?.backTimelineItems ? {id: issue.id, timelineItems: issue.backTimelineItems} : undefined),
    [issue],
  )
  const timelineCoordinates = useMemo(
    () =>
      issue
        ? {
            owner: issue.repository.owner.login,
            repo: issue.repository.name,
            number: issue.number,
          }
        : undefined,
    [issue],
  )
  const initialBackAnchor = useTimelinePrefill({
    coordinates: timelineCoordinates,
    issue: timelineFrontIssue,
    backIssue: timelineBackIssue,
    payloadUpdatedAt: query.dataUpdatedAt,
    feed: payload?.feed,
  })

  if (query.isPending) return <IssueViewerLoading optionConfig={optionConfig} />
  const queryError = getIssueViewerQueryError(query)
  if (queryError) throw queryError
  if (!payload) throw new Error('Issue viewer query returned no payload')

  const data = handleRepositoryDataError(renderedPayload, {
    shouldReloadOnError: !optionConfig.insideSidePanel,
  })
  if (!data) return null

  const resolvedRepository = data?.repository
  const resolvedIssue = resolvedRepository?.issue
  if (!resolvedIssue) {
    const reference = props.isAccountIssue ? props.permalink : `${props.owner}/${props.repo}#${number}`
    throw new Error(`Issue missing from repository data for ${reference}`)
  }

  return (
    <DataRouterIssueViewer
      key={resolvedIssue.id}
      issue={resolvedIssue as unknown as Parameters<typeof DataRouterIssueViewer>[0]['issue']}
      feed={payload.feed}
      initialBackAnchor={initialBackAnchor}
      viewer={data?.safeViewer ?? null}
      isRepoOwnerEnterpriseManaged={resolvedRepository?.isOwnerEnterpriseManaged}
      userNameDisplayConfiguration={resolvedRepository.owner.userNameDisplayConfiguration}
      routeQueryKey={queryOptions.queryKey}
      optionConfig={optionConfig}
    />
  )
}
